import { useCallback, useEffect, useRef, useState } from "react";
import {
    ControllerRenderProps,
    FieldValues,
    Path,
    PathValue,
    useFormContext
} from "react-hook-form";
import { GroupBase, OptionsOrGroups, SingleValue } from "react-select";
import SelectType from "react-select/base";

import { Select } from "@/components/Form/Base/select.component";
import { AutocompleteAddress } from "@/services/bpost.service";
import { getPrefixOfFIeld } from "@/utils/address/address";

import {
    BaseField,
    BaseFieldChildProps,
    BaseFieldProps
} from "../BaseField/BaseField.component";
import { isFormRequired } from "../formField.types";
import { createBPostAddressFieldInputComponent } from "./components/bPostAddressFieldInput.component";
import { createBPostAddressFieldMenuComponent } from "./components/bPostAddressFieldMenu.component";
import { updateCursorPosition } from "./helpers/cursor.helpers";
import { getDisplayValue } from "./helpers/displayValue.helpers";
import { addressToLinkedFieldMapper } from "./mappers/address.mapper";

export type SetOptionsCallback = (
    options: OptionsOrGroups<
        AutocompleteAddress,
        GroupBase<AutocompleteAddress>
    >
) => void;

const filterOption = () => true;

type BPostAddressFieldComponentProperties = {
    placeholder?: string;
    disabled?: boolean;
    onManualEntry: () => void;
    loadOptions: (
        inputValue: string,
        currentAddress: AutocompleteAddress | null,
        callback: SetOptionsCallback
    ) => Promise<void>;
};

export type BPostAddressFieldComponentProps<T extends FieldValues> = Omit<
    BaseFieldProps<T, Path<T>>,
    "required"
> &
    BPostAddressFieldComponentProperties;

const BPostAddressFieldComponent = <T extends FieldValues>({
    placeholder,
    disabled,
    onManualEntry,
    loadOptions,
    test,
    label,
    microCopy,
    name
}: BPostAddressFieldComponentProps<T>) => {
    const { setValue: setFormValue, getValues } = useFormContext<T>();

    const [options, setOptions] = useState<Array<AutocompleteAddress>>([]);
    const [inputValue, setInputValueState] = useState<string>(
        getValues(name) || ""
    );
    const [value, setValueState] = useState<AutocompleteAddress | null>(null);

    const inputValueRef = useRef<string>(getValues(name) || "");
    const valueRef = useRef<AutocompleteAddress | null>(null);
    const selectRef =
        useRef<
            SelectType<
                AutocompleteAddress,
                false,
                GroupBase<AutocompleteAddress>
            >
        >(null);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const setInputValue = useCallback(
        (value: string) => {
            setInputValueState(value);
            inputValueRef.current = value;
        },
        [setInputValueState]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const setValue = useCallback(
        (value: AutocompleteAddress | null) => {
            setValueState(value);
            valueRef.current = value;
        },
        [setValueState]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const setOptionsCallback = useCallback(
        (options: any) => setOptions(options),
        [setOptions]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const handleOnFocus = useCallback(() => {
        const inputValue = getDisplayValue(value) || inputValueRef.current;

        setInputValue(inputValue);
        loadOptions(inputValue, value, setOptionsCallback);
    }, [setInputValue, loadOptions, value]);

    const handleOnBlur = useCallback(() => {
        if (inputValueRef.current === getDisplayValue(valueRef.current)) return;

        setValue(null);
    }, [setValue]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const handleOnChange = useCallback(
        (field: ControllerRenderProps<T, Path<T>>) =>
            (newValue: SingleValue<AutocompleteAddress>) => {
                const fieldValue = getDisplayValue(newValue);

                setValue(newValue);
                setInputValue(fieldValue);

                if (newValue) {
                    const prefix = getPrefixOfFIeld(field.name);
                    const linkedFields =
                        addressToLinkedFieldMapper(prefix)(newValue);
                    linkedFields.forEach(({ field: f, value }) =>
                        setFormValue(f as Path<T>, value)
                    );
                }

                setFormValue(field.name, fieldValue as PathValue<T, Path<T>>);

                setTimeout(() => {
                    updateCursorPosition({
                        address: newValue,
                        inputField: selectRef.current?.inputRef ?? null,
                        openMenu: () => selectRef.current?.openMenu("first")
                    });
                });
            },
        [setValue, setInputValue]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const Input = useCallback(
        createBPostAddressFieldInputComponent({
            placeholder: placeholder,
            setInputValue: setInputValue,
            loadOptions: (inputValue: string) =>
                loadOptions(inputValue, value, setOptionsCallback),
            openMenu: () => selectRef.current?.openMenu("first")
        }),
        [placeholder, value, setInputValue, loadOptions, setOptionsCallback]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const Menu = useCallback(
        createBPostAddressFieldMenuComponent({
            onManualEntry: onManualEntry
        }),
        [onManualEntry]
    );

    const FieldComponent = ({
        field,
        fieldState
    }: BaseFieldChildProps<T, Path<T>>) => {
        // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
        useEffect(() => {
            setFormValue(field.name, inputValue as PathValue<T, Path<T>>);
        }, [inputValue]);

        return (
            <Select<AutocompleteAddress>
                {...field}
                id={field.name}
                instanceId={field.name}
                onChange={handleOnChange(field)}
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}
                value={value}
                classNamePrefix={"bPostAddressField"}
                isInvalid={fieldState.isInvalid}
                isValid={fieldState.isValid}
                inputValue={inputValue}
                blurInputOnSelect={true}
                filterOption={filterOption}
                options={options}
                getOptionLabel={({ string }) => string}
                getOptionValue={({ string }) => string}
                ref={selectRef}
                isDisabled={disabled}
                data-test={test}
                components={{
                    Input: Input,
                    Menu: Menu,
                    NoOptionsMessage: () => null,
                    LoadingMessage: () => null,
                    Placeholder: () => null,
                    SingleValue: () => null,
                    IndicatorsContainer: () => null
                }}
            />
        );
    };

    return (
        <BaseField<T, Path<T>>
            label={label}
            name={name}
            microCopy={microCopy}
            required={true as isFormRequired<T, Path<T>>}
        >
            {FieldComponent}
        </BaseField>
    );
};

export { BPostAddressFieldComponent };
